import { Button, Chip, Grid, Stack, Typography } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { FullWidthField } from "@poseidon/ui/components/form/FullWidthField";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { ImageField } from "@poseidon/ui/components/form/ImageField";
import { MultiLineFullWidthField } from "@poseidon/ui/components/form/MultiLineFullWidthField";
import { Panel } from "@poseidon/ui/components/Panel";
import {
  FormikDisabler,
  usePermissions,
} from "@poseidon/ui/components";
import { storage, uploadFile } from "@poseidon/firebase";
import { getDownloadURL, ref } from "firebase/storage";
import { Field, Formik } from "formik";
import BadgeIcon from "@mui/icons-material/Badge";
import ErrorIcon from "@mui/icons-material/Error";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import * as api from "./api";

function UserAccountStatus({ status, verified, error }) {
  if (status === "created") {
    return (
      <>
        <Chip
          label={`User Account Exists - ${
            verified
              ? "Login enabled"
              : "Login disabled until supplier is verified"
          }`}
          icon={<BadgeIcon />}
          color={verified ? "success" : "warning"}
        />
        <Typography variant="caption" component="div" mt={2}>
          If the user does not have a password yet
          {!verified && ", once verified"} they should use the "Forgot
          password?" link on the dealer portal to pick one. (MVP - future
          development would be to streamline this process)
        </Typography>
      </>
    );
  }
  if (status === "failed") {
    return (
      <>
        <ErrorIcon color="error" />
        &nbsp; User Account Creation Failed: {error}
      </>
    );
  }
  return (
    <>
      <QuestionMarkIcon color="disabled" />
      &nbsp; Unknown Status
    </>
  );
}

export function DealerForm({ dealer }) {
  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();

  const onSubmitHandler = async ({ newLogo, ...values }) => {
    try {
      if (newLogo) {
        const uploadPath = await uploadFile({
          file: newLogo,
          //todo: delete old file
          path: `dealer-logos/${dealer.id}/${newLogo.name}`,
          metadata: {
            contentType: newLogo.type,
          },
        });
        const storageRef = ref(storage, uploadPath);
        const downloadURL = await getDownloadURL(storageRef);
        // remove token for db value so permissions work as intended
        values.logo = downloadURL.split(["&token="])[0];
      }
      await api.updateDealer(dealer.id, values);
      setToast("Successfully updated Dealer");
    } catch (error) {
      setError(error.message);
    }
  };
  const { id, ...initialValues } = dealer;
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmitHandler}>
      {({ values, dirty }) => (
        <Form>
          <FormikDisabler disabled={!canWrite} />
          <Stack
            spacing={5}
            sx={{
              px: 5,
            }}
          >
            <Grid container>
              <Grid item xs={12} sm={8}>
                <Typography variant="h5">Dealer Record</Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  textAlign: "right",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  margin="none"
                  disabled={!dirty || !canWrite}
                >
                  Save
                </Button>
              </Grid>
            </Grid>

            <Panel title="Basic Details">
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="companyName"
                    label="Company Name"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="companyNumber"
                    label="CompanyNumber"
                    margin="none"
                  />
                </Grid>
              </Grid>
            </Panel>
            <Panel title="Logo">
              <ImageField fieldName="newLogo" currentImage={dealer.logo} />
            </Panel>

            <Panel title="Contact Details">
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="contactName"
                    label="Contact Name"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="contactNumber"
                    label="contactNumber"
                    margin="none"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="contactEmail"
                    label="Contact Email"
                    margin="none"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={MultiLineFullWidthField}
                    name="address"
                    label="Address"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="postcode"
                    label="Postcode"
                    margin="none"
                  />
                </Grid>
              </Grid>
            </Panel>

            <Panel title="Account Details">
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="nameOnAccount"
                    label="Name On Account"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="accountNumber"
                    label="AccountNumber"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="sortCode"
                    label="Sort Code"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="iban"
                    label="IBAN (If non-UK)"
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="swiftBic"
                    label="Swift/BIC (if non-UK)"
                    margin="none"
                  />
                </Grid>
              </Grid>
            </Panel>

            <Panel title="User Account Status">
              <UserAccountStatus
                status={values.userAccountStatus}
                error={values.userAccountError}
                verified={values.isVerified}
              />
            </Panel>

            <Panel>
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="createdAt"
                    label="Created At"
                    margin="none"
                    value={values.createdAt?.toLocaleString()}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="updatedAt"
                    label="Updated At"
                    margin="none"
                    value={values.updatedAt?.toLocaleString()}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Panel>

            {dirty && (
              <Panel title="Unsaved changes">
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    margin="normal"
                  >
                    Save
                  </Button>
                </Grid>
              </Panel>
            )}
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
